import {
    Card,
    Heading,
    DescriptionList,
    Layout,
    ResourceList,
    ResourceItem,
    TextStyle,
    Frame,
    Page,
    Pagination,
    EmptyState,
    Badge,
    Button,
    Stack,
    Spinner,
    TextField,
    Icon,
    Form,
    ButtonGroup,
} from '@shopify/polaris';
import {useCallback, useEffect} from 'react';
import { useState } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import Api from '../../helpers/api';
import CustomerCreditCard from './CustomerCreditCard';
import {SearchMajor} from "@shopify/polaris-icons";

const LocationsPaginator = ({ locations = [], locationsMeta, getLocations }) => {
    if (! locations.length) {
        return null;
    }

    return (
        <Card.Section>
            <Pagination
                label={`Showing Page ${locationsMeta.current_page} of ${locationsMeta.last_page}`}
                hasPrevious={ locationsMeta.current_page > 1}
                onPrevious={() => { getLocations(locationsMeta.current_page - 1) }}
                hasNext={ locationsMeta.current_page < locationsMeta.last_page }
                onNext={() => { getLocations(locationsMeta.current_page + 1) }} />
        </Card.Section>
    )
}

const AddressBlock = ({ location, label }) => location
    ? (
        <div>
            <h3><TextStyle variation="strong">{ label }</TextStyle></h3>
            {location.company && <>{location.company}<br /></>}
            {location.address1}<br />
            {location.address2}<br />
            {location.address3 && <>{location.address3}<br /></>}
            {location.city}, {location.state} {location.zip_code}
            <p>
                { location.type && <div><TextStyle variation="strong">Type:</TextStyle> {location.type || 'Not Defined'}</div>}
            </p>
        </div>
    )
    : null;

const LocationsEmptyState = ({ locations }) => {
    if (locations.length) {
        return null;
    }

    return (<EmptyState heading="The customer has no additional locations"></EmptyState>)
}

const OnHoldToggle = ({ customer, setCustomer }) => {
    const [loading, setLoading] = useState(true);
    const [hasOrders, setHasOrders] = useState(true);
    const history = useHistory();

    useEffect(() => {
        setLoading(true);

        Promise
            .all([
                Api.hasCustomerOrders(customer.id),
            ])
            .then(results => {
                setHasOrders(results[0].data.status ?? false)
            })
            .finally(() => setLoading(false));
    }, [customer.id]);

    const handleDelete = useCallback(() => {
        if (loading) {
            return;
        }

        setLoading(true);
        const confirmation = confirm("Are you sure you want to delete this customer?");

        if (confirmation) {
            Api.deleteCustomer(customer.id).then(() => {
                history.goBack();
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
    }, [loading, customer]);

    return (
        <Card sectioned>
            <ButtonGroup>
                {!hasOrders && (
                    <Button destructive={true} onClick={handleDelete} loading={loading}>
                        Delete customer
                    </Button>
                )}
            </ButtonGroup>
        </Card>
    )
}

const LocationsCard = ({ customer, placeOrder }) => {
    if (! customer.id) {
        return null;
    }

    const history = useHistory();
    const [locations, setLocations] = useState([]);
    const [locationsMeta, setLocationsMeta] = useState({});
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    const getLocations = useCallback((page = 1, query = null) => {
        let searchParam = query ?? search;

        if (searchParam && searchParam.length > 0 && searchParam.length < 3) {
            alert("You need to type at least 3 characters");
            return;
        }

        setLoading(true);
        Api
            .getLocations(customer.id, page, searchParam)
            .then(({ data }) => {
                setLocations(data.data);
                setLocationsMeta(data.meta);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [locations, locationsMeta, loading, search, customer.id])

    useEffect(() => {
        getLocations(1);
    }, [customer.id]);

    return (
        <Card
            title="Locations"
            sectioned
            actions={[
                { content: 'Add location', onAction: () => history.push(`/customers/${customer.id}/locations/create`) },
            ]}>

            <Card.Section>
                <Form
                    onSubmit={() => {
                        getLocations(1);
                    }}
                >
                    <TextField
                        placeholder={"Search locations"}
                        value={search}
                        onChange={setSearch}
                        clearButton
                        onClearButtonClick={() => {
                            setSearch('');
                            getLocations(1, '');
                        }}
                        connectedRight={(
                            <Button
                                primary
                                onClick={() => {
                                    getLocations(1)
                                }}
                            >
                                <Icon source={SearchMajor} />
                            </Button>
                        )}
                    />
                </Form>
            </Card.Section>

            <LocationsPaginator locations={locations}
                                locationsMeta={locationsMeta}
                                getLocations={getLocations} />

            <Card.Section>
                { loading && (
                    <Spinner size={"small"} />
                )}
                { !loading && (
                    <ResourceList
                        resourceName={{singular: 'Location', plural: 'Locations'}}
                        items={locations}
                        emptyState={<LocationsEmptyState locations={locations} />}
                        renderItem={(location) => {
                            const shortcutActions = [
                                {
                                    content: 'Place Order',
                                    accessibilityLabel: `Place order for location ${location.id}`,
                                    onAction: () => { placeOrder(location.id); }
                                }
                            ]

                            const addresses = [
                                {location: location.billing, label: 'Billing Address'},
                                {location: location.shipping, label: 'Shipping Address'},
                            ].filter(item => item.location);

                            const badges = [
                                location.default && <Badge size='small'>Default</Badge>,
                            ].filter(Boolean);

                            return (
                                <ResourceItem onClick={ () => { history.push(`/locations/${location.id}`); }} shortcutActions={shortcutActions}>
                                    <Stack vertical>
                                        { location.phone && <div><TextStyle variation="strong">Phone: </TextStyle> {location.phone}</div> }

                                        <Stack>
                                            {addresses.map((item, i) => (<AddressBlock {...item} key={`location-${i}`} />))}
                                        </Stack>
                                        <div>
                                            <h3><TextStyle variation="strong">Notes</TextStyle></h3>
                                            {location.notes}
                                        </div>
                                    </Stack>
                                </ResourceItem>
                            )
                        }}
                    />
                )}
            </Card.Section>

            <LocationsPaginator locations={locations}
                                locationsMeta={locationsMeta}
                                getLocations={getLocations} />
        </Card>
    );
}

const ShowCustomer = () => {
    const history = useHistory();
    const [customer, setCustomer] = useState(null);
    const {customerId} = useParams();
    const {search} = useLocation();
    const [backUrl] = useState(
        (new URLSearchParams(search)).get('back') || null
    );

    const placeOrder = (location_id) => {
        history.push(`/orders/${customer.id}?location_id=${location_id}`);
    }

    useEffect(() => {
        getCustomer(customerId);
    }, [customerId]);

    const getCustomer = (customerId) => {
        Api.getCustomer(customerId).then( response => {
            setCustomer(response.data.data);
        });
    }

    const editCustomer = () => {
        history.push(`/customers/${customerId}/edit`);
    }

    const onCustomerUpdate = useCallback((data) => {
        setCustomer(data.customer);
    });

    const primaryFooterAction = customer?.default_location
        ? { content: 'Place Order', onAction: () => { placeOrder(customer.default_location.id) } }
        : null;

    const primaryAction = (customer?.status === 'approved')
        ? {
            content: 'Edit Customer',
            onAction: editCustomer,
        }
        : null;

    const shippingMethod = customer?.shipping_method
        ? `${customer.shipping_method.name} - $${customer.shipping_method.price}`
        : null;

    const getCustomerFinancialInformation = () => {
        return [
            { term: 'Total Unpaid', 'description': `$${(customer.orders_sum_total_outstanding ?? 0).toFixed(2)}` },
        ];
    }

    return customer && (
        <Frame>
            <Page title={`Customer`}
                  primaryAction={primaryAction}
                  breadcrumbs={[
                      { content: 'Go back', onAction: () => history.goBack() },
                  ]}
            >
                <Layout>
                <Layout.Section>
                    <Card title="Account Information" sectioned primaryFooterAction={primaryFooterAction}>
                        <DescriptionList items={[
                            { term: 'Shopify Customer ID', 'description': customer.shopify_customer_id },
                            { term: 'Email', 'description': customer.email },
                            { term: 'Phone', 'description': customer.phone_number },
                            { term: 'Name', 'description': `${customer.additional_data.first_name} ${customer.additional_data.last_name}` },
                            { term: 'Default Shipping Method', 'description': shippingMethod },
                        ]} />
                        <Heading>Financial Information</Heading>
                        <DescriptionList items={getCustomerFinancialInformation()} />
                    </Card>

                    <LocationsCard customer={customer} placeOrder={placeOrder} />
                </Layout.Section>
                <Layout.Section secondary>
                    <OnHoldToggle customer={customer} setCustomer={setCustomer} />
                    <CustomerCreditCard
                        customer={customer}
                        onUpdate={onCustomerUpdate}
                        type="customer"
                        card={customer.card}
                    />
                    <Card sectioned title="Notes">
                        <div>{customer.additional_data.notes}</div>
                    </Card>
                </Layout.Section>
            </Layout>
            </Page>
        </Frame>
    );
}

export default ShowCustomer;
